import CollectionCard from "@/components/Bundle/Collection/CollectionCard";
import CreateCollectionCard from "@/components/Bundle/Collection/CreateCollectionCard";
import { useCollections } from "@/hooks/useCollection";
import { useMetaInfos } from "@/hooks/useMeta";
import useTranslation from "@/hooks/useTranslation";
import { ICollectionOrderField } from "@/interfaces/bundle.interface";
import CommonLayout from "@/layouts/Common";
import { collectionsState } from "@/stores/collection";
import { currentUserState } from "@/stores/common";
import { smoothScrollToTarget } from "@/utils/scroll.util";
import { Button, IconButton, Menu, MenuItem } from "@material-ui/core";
import ArrowBackIosIcon from "@material-ui/icons/ArrowBackIos";
import ArrowForwardIosIcon from "@material-ui/icons/ArrowForwardIos";
import { Spin } from "antd";
import { NextPage } from "next";
import Head from "next/head";
import { useRouter } from "next/router";
import React, { useMemo, useState } from "react";
import { Element } from "react-scroll";
import { useRecoilValue } from "recoil";
import styles from "./styles.module.less";

interface IProps {
  page: number;
  size: number;
}

const Collection: NextPage<IProps> = (props) => {
  const { page, size } = props;
  const { loading, refetch, field, setField } = useCollections(props);
  const collections = useRecoilValue(collectionsState);
  const currentUser = useRecoilValue(currentUserState);
  const refresh = async () => {
    await refetch();
  };
  const { t } = useTranslation("bundle");

  const router = useRouter();

  const { forward, backward } = useMemo(() => {
    const count = collections.count;
    const currentMax = page * size;
    const cursor = { forward: true, backward: false };
    if (currentMax >= count) {
      cursor.forward = false;
    }
    if (page > 1) {
      cursor.backward = true;
    }
    return cursor;
  }, [collections.count, page, size]);

  const handleForward = async () => {
    await smoothScrollToTarget("collection-content");
    await router.push(`${router.pathname}?page=${page + 1}&size=${size}`);
  };

  const handleBackward = async () => {
    await smoothScrollToTarget("collection-content");
    await router.push(`${router.pathname}?page=${page - 1}&size=${size}`);
  };

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (field?: ICollectionOrderField) => {
    if (field) {
      setField(field);
    }
    setAnchorEl(null);
  };

  useMetaInfos(collections?.results?.map((collection) => collection.id));

  return (
    <CommonLayout>
      <Head>
        <title>
          {t("menu:bundle.collection")} - {t("menu:bundle.itself")} - MedicGo
        </title>
      </Head>
      <div className={styles.wrapperLeft}>
        {currentUser?.id && <CreateCollectionCard refresh={refresh} />}
        <div>
          <Button onClick={handleClick} variant="outlined" color="default">
            {t("sub.order.itself")}
          </Button>
          <Menu
            anchorEl={anchorEl}
            keepMounted
            open={Boolean(anchorEl)}
            onClose={() => handleClose()}
          >
            <MenuItem
              onClick={() => handleClose(ICollectionOrderField.UPDATED_TIME)}
              selected={field === ICollectionOrderField.UPDATED_TIME}
            >
              {t("collection.order.update")}
            </MenuItem>
            <MenuItem
              onClick={() => handleClose(ICollectionOrderField.VIEWED_TIMES)}
              selected={field === ICollectionOrderField.VIEWED_TIMES}
            >
              {t("collection.order.times")}
            </MenuItem>
          </Menu>
        </div>
        <div className={styles.wrapper}>
          {backward && (
            <IconButton onClick={handleBackward}>
              <ArrowBackIosIcon />
            </IconButton>
          )}
          {forward && (
            <IconButton onClick={handleForward}>
              <ArrowForwardIosIcon />
            </IconButton>
          )}
        </div>
      </div>
      <Spin spinning={loading && collections?.results?.length === 0}>
        <Element name="collection-content">
          <div className={styles.grid}>
            {collections?.results?.map((collection) => (
              <CollectionCard
                collection={collection}
                loading={loading}
                key={collection.id}
              />
            ))}
          </div>
        </Element>
        <div className={styles.wrapper}>
          {backward && (
            <IconButton onClick={handleBackward}>
              <ArrowBackIosIcon />
            </IconButton>
          )}
          {forward && (
            <IconButton onClick={handleForward}>
              <ArrowForwardIosIcon />
            </IconButton>
          )}
        </div>
      </Spin>
    </CommonLayout>
  );
};

Collection.getInitialProps = async (ctx) => {
  const page = parseInt((ctx.query.page as string) || "1", 10);
  const size = parseInt((ctx.query.size as string) || "10", 10);
  return {
    page,
    size,
    namespacesRequired: ["common", "menu", "bundle"],
  };
};

export default Collection;
